import React, { useEffect } from 'react';
import {
  LuShieldCheck, LuLock, LuDownload, LuDatabase, LuEyeOff,
  LuUserCheck, LuCamera, LuUpload, LuMessageSquare, LuUsers,
  LuCircleAlert, LuCircleCheck
} from 'react-icons/lu';
import PublicLayout from './PublicLayout';

export default function Privacy() {
  useEffect(() => {
    document.title = 'Privacy Policy | Photo Harbor';
  }, []);

  return (
    <PublicLayout>
      {/* Hero */}
      <section className="lp-hero" style={{ paddingBottom: '2rem' }}>
        <div className="lp-hero-inner">
          <div className="lp-trust-icon" style={{ margin: '0 auto 1.5rem', width: 56, height: 56 }}>
            <LuShieldCheck size={28} />
          </div>
          <h1 className="lp-h1">Privacy, by Design</h1>
          <p className="lp-hero-sub">
            Photo Harbor is subscription-funded. We work for you, not advertisers. We do not sell your data
            and we do not train AI models on your photos.
          </p>
        </div>
      </section>

      {/* Principles */}
      <section className="lp-features" style={{ paddingTop: '2rem' }}>
        <div className="lp-section-inner">
          <div className="lp-section-head">
            <h2>How we think about privacy</h2>
          </div>
          <div className="lp-features-grid" style={{ maxWidth: 900, margin: '0 auto' }}>
            {[
              { icon: LuCircleCheck, title: 'Subscription-funded', desc: 'No ads, no data resale. You pay for storage and features, not surveillance.' },
              { icon: LuLock, title: 'Private by default', desc: 'Your photos start private. You decide what to share, who can see it, and when access expires.' },
              { icon: LuDownload, title: 'Ownership & export', desc: 'Download or delete your library anytime. You are never locked in.' }
            ].map((item) => (
              <div key={item.title} className="lp-feature-card">
                <div className="lp-feature-icon"><item.icon size={20} /></div>
                <h3>{item.title}</h3>
                <p>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What we collect */}
      <section className="lp-trust">
        <div className="lp-section-inner">
          <div className="lp-section-head">
            <h2>What information we collect</h2>
            <p>We only collect what we need to run the service.</p>
          </div>
          <div className="lp-trust-grid">
            {[
              { icon: LuUserCheck, title: 'Account details', desc: 'Email, password (hashed), and basic profile info. No unnecessary personal data.' },
              { icon: LuCamera, title: 'Your photos & metadata', desc: 'Files you upload plus technical metadata (size, dimensions, format) to display and process them.' },
              { icon: LuDatabase, title: 'Usage & diagnostics', desc: 'Basic logs to keep the service stable and secure, not ad tracking.' },
              { icon: LuShieldCheck, title: 'Billing', desc: 'Payments handled by Stripe. We do not store full card details on our servers.' }
            ].map((item) => (
              <div key={item.title} className="lp-trust-card">
                <div className="lp-trust-icon"><item.icon size={22} /></div>
                <h3>{item.title}</h3>
                <p>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What we don't do */}
      <section className="lp-features">
        <div className="lp-section-inner">
          <div className="lp-section-head">
            <h2>What we don't do</h2>
          </div>
          <div className="lp-trust-grid">
            {[
              { icon: LuEyeOff, title: 'No data sales', desc: 'We never sell or rent your data to advertisers or third parties.' },
              { icon: LuShieldCheck, title: 'No AI training', desc: 'Your uploads are not training material. If we add smart tools, they will be opt-in.' },
              { icon: LuUsers, title: 'No ad profiles', desc: 'We don\'t build advertising profiles or track you across the web.' },
              { icon: LuLock, title: 'No hidden sharing', desc: 'You control every share. We never publish or market your content.' }
            ].map((item) => (
              <div key={item.title} className="lp-trust-card">
                <div className="lp-trust-icon"><item.icon size={22} /></div>
                <h3>{item.title}</h3>
                <p>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How we use data */}
      <section className="lp-trust">
        <div className="lp-section-inner">
          <div className="lp-section-head">
            <h2>How we use your information</h2>
            <p>Everything we do with your data supports the service you pay for.</p>
          </div>
          <div className="lp-features-grid" style={{ maxWidth: 900, margin: '0 auto' }}>
            {[
              { icon: LuCamera, title: 'Deliver the service', desc: 'Upload, organize, and share your photos with full control over access.' },
              { icon: LuShieldCheck, title: 'Protect accounts', desc: 'Security monitoring, rate limits, and abuse detection keep your library safe.' },
              { icon: LuUpload, title: 'Process files', desc: 'We generate responsive variants and previews so galleries load fast on any device.' },
              { icon: LuMessageSquare, title: 'Essential communication', desc: 'Account updates, billing notices, and support, no marketing spam.' }
            ].map((item) => (
              <div key={item.title} className="lp-feature-card">
                <div className="lp-feature-icon"><item.icon size={20} /></div>
                <h3>{item.title}</h3>
                <p>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Security */}
      <section className="lp-features">
        <div className="lp-section-inner">
          <div className="lp-section-head">
            <h2>How we protect you</h2>
            <p>Security is built into every layer of the platform.</p>
          </div>
          <div className="lp-trust-grid">
            {[
              { icon: LuLock, title: 'Encrypted in transit', desc: 'All traffic uses HTTPS. Your content is protected in transit.' },
              { icon: LuShieldCheck, title: 'Access controls', desc: 'Only authorized systems and essential staff can access data, and access is logged.' },
              { icon: LuUsers, title: 'Trusted processors', desc: 'Vetted infrastructure partners for storage, delivery, and payments.' },
              { icon: LuCircleAlert, title: 'Incident transparency', desc: 'If something goes wrong, we notify you promptly and explain what happened.' }
            ].map((item) => (
              <div key={item.title} className="lp-trust-card">
                <div className="lp-trust-icon"><item.icon size={22} /></div>
                <h3>{item.title}</h3>
                <p>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact */}
      <section className="lp-final-cta">
        <div className="lp-section-inner">
          <h2>Questions about privacy?</h2>
          <p>Email us at <a href="mailto:support@dailey.photos" style={{ color: '#304849', fontWeight: 600 }}>support@dailey.photos</a>. We typically respond within 24 hours.</p>
          <p style={{ fontSize: '0.8125rem', color: '#5f7270', marginTop: '1.5rem' }}>Last updated: January 31, 2026</p>
        </div>
      </section>
    </PublicLayout>
  );
}
